import type { Attrs, EventListeners, Reactive } from "minze";
import { MinzeElement } from "minze";
import { EMAIL_REGEX, PHONE_REGEX } from "../../modules/utils";
import "./locale";
import { submitForm } from "../../constants";
import { nanoid } from "nanoid";
import { SquidexTextLabels } from '../../modules/squidex/labels';

export interface TjCvFileUpload {
  values: { [fieldName: string]: any };
  errors: { [fieldName: string]: string };
  submitted: boolean;
  formSubmitError: boolean;
}

export interface TjCvFile extends File {
  id: string;
}

export class TjCvFileUpload extends MinzeElement {
  attrs: Attrs = [["data-job-title", "" as string]];
  reactive: Reactive = [
    ["errors", {}],
    ["values", {}],
    ["submitted", false],
    ["formSubmitError", false],
  ];
  getLocaleKey = (key: string) => {
    return SquidexTextLabels.getKey(`cvFileUpload.${key}`);
  };
  onFileChange = (e: Event) => {
    const fileInput = e.target as HTMLInputElement;
    if (this.values?.files?.length >= 2) {
      this.errors = {
        ...this.errors,
        files: this.getLocaleKey("errors.fileCount"),
      };
    } else if (!this.values.files) {
      let file = fileInput.files?.[0] as TjCvFile;
      file.id = nanoid();
      this.values = { ...this.values, files: [file] };
      this.errors = { ...this.errors, files: "" };
    } else {
      let file = fileInput.files?.[0] as TjCvFile;
      file.id = nanoid();
      this.values = { ...this.values, files: this.values.files.concat(file) };
      this.errors = { ...this.errors, files: "" };
    }
    if (this.values.files && this.values.files.length > 0) {
      this.validateFileUpload();
    }
    this.rerender();
  };
  onFileRemove = (e: Event) => {
    // Filter out the file that was removed but using data-file-id attribute
    const fileToRemove = (e.target as HTMLElement).getAttribute("data-file-id");
    this.values = {
      ...this.values,
      files: this.values.files.filter((file: any) => file.id !== fileToRemove),
    };
    this.errors = { ...this.errors, files: "" };
    this.validateFileUpload();
    this.rerender();
  };
  onBlur = (e: Event) => {
    const input = e.target as HTMLInputElement;
    this.values = { ...this.values, [input.id]: input.value };
  };
  // I would love to extract the validation logic to a reusable function
  // For now this will do.
  validateFileUpload = () => {
    // Check if a file is selected
    if (!this.values.files) {
      this.errors = { ...this.errors, files: this.getLocaleKey("errors.file") };
      return;
    }

    // Check file size && file extension of each file
    const allowedExtensions = /(\.doc|\.docx|\.pdf)$/i;
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    this.values.files.forEach((file: any) => {
      if (!allowedExtensions.exec(file.name)) {
        this.errors = {
          ...this.errors,
          files: this.getLocaleKey("errors.fileType"),
        };
        return;
      }
      if (file.size > maxSizeInBytes) {
        this.errors = {
          ...this.errors,
          files: this.getLocaleKey("errors.fileSize"),
        };
        return;
      }
    });
  };

  // I would love to extract the validation logic to a reusable function
  // For now this will do.
  validateForm = () => {
    if (!this.values.name)
      this.errors = { ...this.errors, name: this.getLocaleKey("errors.name") };
    if (!this.values.email)
      this.errors = {
        ...this.errors,
        email: this.getLocaleKey("errors.email"),
      };
    else if (!EMAIL_REGEX.test(this.values.email)) {
      this.errors = {
        ...this.errors,
        email: this.getLocaleKey("errors.invalidEmail"),
      };
    }
    if (!this.values.phone)
      this.errors = {
        ...this.errors,
        phone: this.getLocaleKey("errors.phone"),
      };
    else if (!PHONE_REGEX.test(this.values.phone)) {
      this.errors = {
        ...this.errors,
        phone: this.getLocaleKey("errors.invalidPhone"),
      };
    }
    this.validateFileUpload();
    this.rerender();
  };
  onSubmit = async (e: Event) => {
    e.preventDefault(); // Prevent default form submission
    this.errors = {};
    this.formSubmitError = false;
    this.validateForm();
    if (!Object.values(this.errors).length) {
      const formData = new FormData();
      formData.append("jobTitle", this.dataJobTitle as string);
      for (let key in this.values) {
        if (key === "files") {
          this.values.files.forEach((file: any, i: any) => {
            formData.append(`file${i}`, file);
          });
        } else {
          formData.append(key, this.values[key]);
        }
      }
      try {
        const webhookId = "3ey07fi"; // https://hooks.zapier.com/hooks/catch/12979755/3ey07fi/
        const response = await submitForm(webhookId, formData);
        if (response.status === "success") {
          this.submitted = true;
          this.clearForm();
          this.rerender();
        } else {
          throw new Error("POST Request Failed");
        }
      } catch (err) {
        console.error(err);
        this.formSubmitError = true;
        this.clearForm();
        this.rerender();
      }
    }
  };
  clearForm = () => {
    this.values = {};
    this.errors = {};
  };

  html = () => `
    ${
      !this.submitted
        ? /*html*/ `
      <h2>${this.getLocaleKey("heading")}</h2>
      <form id="cvUploadForm" enctype="multipart/form-data">
      <tj-input type="text" id="name" placeholder="${this.getLocaleKey("fieldLabels.name")}" error="${this.errors["name"]}" value="${
        this.values["name"]
      }"></tj-input>
      <tj-input type="email" id="email" placeholder="${this.getLocaleKey("fieldLabels.email")}" error="${this.errors["email"]}" value="${
        this.values["email"]
      }"></tj-input>
      <tj-input type="phone" id="phone" placeholder="${this.getLocaleKey("fieldLabels.phone")}" error="${this.errors["phone"]}" value="${
        this.values["phone"]
      }"></tj-input>
      <tj-input id="message" textarea="true" placeholder="${this.getLocaleKey("fieldLabels.message")}" value="${
        this.values["message"]
      }"></tj-input>
      <div>
        <p>${this.getLocaleKey("fieldLabels.uploadFileDescription")}</p>
        <div class="fileUploadWrapper">
          <label for="cvFileInput" class="custom-file-upload">
            <input type="file" id="cvFileInput" name="cvFileInput" value="${this.values["file"]}" accept=".doc, .docx, .pdf" multiple>
            ${this.getLocaleKey("fieldLabels.attach")}
          </label>
          <div class="requirementText">${this.getLocaleKey("fieldLabels.fileType")}</div>
        </div>
        ${this.errors.files ? `<div id="fileErrorMessage">${this.errors.files}</div>` : ""}
        ${
          this.values.files
            ? this.values.files
                .map((file: any) => {
                  return `<div data-file-uid="${file.id}"id="fileDisplay">${file.name} <span id="removeFile" data-file-id="${file.id}">X</span></div>`;
                })
                .join("")
            : ""
        }
      </div>
      <button id="submit" type="submit" class="tj-cv-file-upload-submit-button"
      >${this.getLocaleKey("submit")}</button>
      ${this.formSubmitError ? `<div id="formSubmitError">${this.getLocaleKey("errors.formSubmitError")}</div>` : ""}
    </form>
    `
        : `<div id="successMessage">${this.getLocaleKey("successMessage")}</div>`
    }`;
  css = () => `
    :host {
      max-width: 510px;
      width: 100%;
    }
    input[type="file"] {
      display: none;
    }
    .requirementText {
      font-size: 12px;
    }
    .custom-file-upload {
      display: inline-block;
      padding: 9px 17px;
      cursor: pointer;
      background: #068FC3;
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      border-radius: 12px;
      transition: filter 0.1s ease-in-out, box-shadow 200ms ease, transform 0.1s;
    }
    .custom-file-upload:hover, #submit:hover, #removeFile:hover {
      box-shadow: 0px 4.2px 4.2px 0px rgba(0, 0, 0, 0.15);
      transform: scale(1.05);
    }
    #cvUploadForm {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
    #submit {
      margin-top: 1rem;
      padding: 13px 40px;
      background: #068FC3;
      color: #fff;
      font-weight: 700;
      border-radius: 14px;
      cursor: pointer;
      border: none;
      font-size: 21px;
      font-family: inherit;
      transition: filter 0.1s ease-in-out, box-shadow 200ms ease, transform 0.1s;
    }
    .fileUploadWrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    }
    #fileDisplay {
      margin-top: 1rem;
    }
    #removeFile {
      cursor: pointer;
      color: white;
      display: inline-block;
      width: 20px;
      height: 20px;
      margin-left: 2rem;
      background: red;
      border-radius: 999px;
      font-size: 12px;
      line-height: 1.7;
      text-align: center;
      font-family: inherit;
      transition: filter 0.1s ease-in-out, box-shadow 200ms ease, transform 0.1s;
    }
    #fileErrorMessage {
      color: #DF374B;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%;
    margin: 4px 0 0;
    }
    #successMessage {
      font-size: 21px;
      font-weight: 700;
      color: #133762;
    }
  `;
  eventListeners: EventListeners = [
    ["#cvUploadForm", "submit", this.onSubmit],
    ["#name", "blur", this.onBlur],
    ["#email", "blur", this.onBlur],
    ["#phone", "blur", this.onBlur],
    ["#message", "blur", this.onBlur],
    ["#cvFileInput", "change", this.onFileChange],
    ["#removeFile", "click", this.onFileRemove],
  ];
}
